import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import * as XLSX from 'xlsx';
import { CargoAsociadoAsociacion, CargosService, ResponseCargos, ResponseCargosAsociados } from '../../../../api';
import { FormularioService } from '../../../services/formulario.service';
import { EjercicioVigenteService } from '../../../services/ejercicio-vigente.service';
import { NavegacionService } from '../../../services/navegacion.service';
import { MigasService } from '../../../services/migas.service';
import { mensajeErrorCarga } from '../../../core/http-errors';
import { ErrorFichaComponent } from '../../error-ficha/error-ficha.component';
import { AlertService } from '../../alert/alert.service';
import { Field } from '../../formulario/field.model';
import { Filter } from '../../formulario/filter-model';
import { FORM_BUTTONS } from '../../formulario/form-buttons.config';
import { FormButtonType } from '../../formulario/formButtonType.enum';
import { FormularioComponent } from '../../formulario/formulario.component';
import { BodyRequest, FunctionsAvailable, ItemType } from '../../formulario/request.model';
import { FormEvent, ItemEvent } from '../../item/item.model';
import { ListadoComponent } from '../../listado/listado.component';
import { SpinnerComponent } from '../../spinner/spinner.component';

import { TabsNavDirective } from '../../ui/tabs-nav.directive';

export enum ViewMode {
  FOGUERES = 'fogueres',
  BARRACAS = 'barracas',
}

export interface DataRow {
  dni: string;
  idAsociacion: number;
  idCargo: number;
  idAsociado: number;
  nombre: string;
  apellidos: string;
  asociacion: string;
}

@Component({
  selector: 'app-cargo',
  standalone: true,
  imports: [CommonModule, FormularioComponent, ListadoComponent, SpinnerComponent, ErrorFichaComponent, TabsNavDirective],
  templateUrl: './cargo.component.html',
  styleUrl: './cargo.component.scss'
})
export class CargoComponent {
  ViewMode = ViewMode;
  ItemType = ItemType;
  FunctionsAvailable = FunctionsAvailable;

  loading = true;

  @Input()
  item!: any;

  @Input()
  parent?: any;

  @Output()
  openItem = new EventEmitter<ItemEvent | FormEvent>();

  @Output()
  back = new EventEmitter<any>();

  viewMode: ViewMode = ViewMode.FOGUERES;
  preUrl: string = '';
  error = '';
  /** Ejercicio del que se muestran los titulares (el vigente de Censo). */
  ejercicioMostrado: number | null = null;

  asociados: CargoAsociadoAsociacion[] = [];

  showEdit = false;
  buttons = [
    FORM_BUTTONS[FormButtonType.Borrar],
    FORM_BUTTONS[FormButtonType.Cancelar],
  ]
  fields: Field[] = [];
  type!: ItemType;

  filter: Filter[] = [{
    name: 'tipoAsociaciones',
    condition: 2
  }]

  constructor(
    private cargosService: CargosService,
    private formularioService: FormularioService,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private ejercicioVigente: EjercicioVigenteService,
    private nav: NavegacionService,
    private migas: MigasService
  ) {

  }

  ngOnInit() {
    let id = this.route.snapshot.paramMap.get('id');
    this.preUrl = this.route.snapshot.url[0].path;
    this.loadData(id!);
  }

  changeMode(mode: ViewMode) {
    this.viewMode = mode;
    this.filter[0].condition = mode === ViewMode.FOGUERES ? 2 : 1;
  }

  loadData(id: string) {
    this.error = '';
    if (!Boolean(id)) {
      id = this.item?.id;
    }
    if (Boolean(id) && id !== '-1') {
      this.loading = true;
      let cargoData = this.cargosService.cargosGetById(Number(id));
      // Antes el ejercicio estaba fijo en el ID 13 (2026): los titulares salían del ejercicio equivocado. Ahora, el vigente.
      const asociados$ = this.ejercicioVigente.obtener().pipe(switchMap(vigente => {
        this.ejercicioMostrado = vigente?.ejercicio ?? null;
        return vigente ? this.cargosService.getAsociadosByCargoAndEjercicio(Number(id), vigente.id) : of({ status: { status: 200 }, asociados: [] } as ResponseCargosAsociados);
      }));
      forkJoin([asociados$, cargoData]).subscribe({
        next: ([asociados, cargo]: [ResponseCargosAsociados, ResponseCargos]) => {
          if (cargo.status?.status !== 200 || !cargo.cargos?.[0]) {
            this.fallar('El cargo no existe.');
            return;
          }
          this.item = cargo.cargos![0];
          this.migas.etiqueta('cargo:' + id, this.item.nombre);
          if (asociados.status?.status === 200) {
            // La especificación histórica usa el plural, pero algunas respuestas
            // anteriores devolvieron el campo en singular. Normalizamos el dato
            // antes de que ListadoComponent aplique sus filtros.
            this.asociados = (asociados.asociados ?? []).map(asociado => ({
              ...asociado,
              tipoAsociaciones: asociado.tipoAsociaciones ?? asociado['tipoAsociacion']
            }));
          }
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => this.fallar(mensajeErrorCarga(err, 'el cargo'))
      });
    } else {
      this.fallar('El cargo no existe.');
    }
  }

  private fallar(mensaje: string) {
    this.error = mensaje;
    this.loading = false;
    this.alertService.danger(mensaje, 5000);
  }

  volverAlListado() {
    this.nav.volverAlListado('/cargos');
  }

  reintentar() {
    this.loadData(String(this.item?.id ?? this.route.snapshot.paramMap.get('id') ?? ''));
  }

  editItem() {
    this.loading = true;
    let { fields, buttons, titleForm, mode, type } = this.formularioService.loadFormConfig(FunctionsAvailable.cargosPut, this.buttons, this.item, 'Editar', ItemType.CARGOS);
    this.fields = fields;
    this.buttons = buttons;
    this.type = type;
    this.loading = false;
    this.showEdit = true;
    // this.openItem.emit({item: this.item, type: ItemType.EJERCICIOS as ItemType, functionSelected: FunctionsAvailable.ejerciciosPut});
  }

  goBack(event?: any) {
    if (this.back.observed) {
      this.back.emit(event);
    } else {
      this.nav.volver('/cargos');
    }
  }

  handleClick(section: ViewMode) {
    this.viewMode = section;
  }

  emitOpenItem(event: any, type: string, parent: BodyRequest) {
    this.openItem.emit({ item: event, type: type as ItemType, parent: parent });
  }

  prepareDataExcel(filter: Filter) {
    return this.asociados.filter(item => Number(item[filter.name]) === Number(filter.condition));
  }

  getDataExcel(data: CargoAsociadoAsociacion[]): DataRow[] {
    let dataRows: DataRow[] = [];
    for (let asociado of data) {
      let dataRow: DataRow = {
        dni: asociado.nif,
        idAsociacion: asociado.idAsociacion,
        idCargo: asociado.idCargo,
        idAsociado: asociado.id,
        nombre: asociado.nombre,
        apellidos: asociado.apellidos,
        asociacion: asociado.nombreAsociacion
      }
      dataRows.push(dataRow);
    }
    return dataRows;
  }

  downloadExcel() {
    let fileName = this.item.nombre.toLowerCase() + '.xlsx';

    let fogueresData = this.prepareDataExcel({ name: this.filter[0].name, condition: 2 });
    let barraquesData = this.prepareDataExcel({ name: this.filter[0].name, condition: 1 });

    const wsFogueres: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.getDataExcel(fogueresData));
    const wsBarraques: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.getDataExcel(barraquesData));

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsFogueres, 'Fogueres');
    XLSX.utils.book_append_sheet(wb, wsBarraques, 'Barraques');

    XLSX.writeFile(wb, fileName);
  }

}
